import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Maximize2Icon, MousePointerClickIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { SectionHeadline } from '../SectionHeadline';
import { EmbedWindow } from '../EmbedWindow';
import { ivfPulseMoments, ivfPulseUrl } from '../../../data/deck';
export function CongressSection() {
  const [fullOpen, setFullOpen] = useState(false);
  return <Section id="congress" data-id="element-473" data-mp="1scs8mc20knrp">
      <AnimatePresence data-id="element-474" data-mp="1nglctc3c0snh">
        {fullOpen && <EmbedWindow key="ivf-pulse" url={ivfPulseUrl} title="The IVF Pulse — ESHRE 2027 campaign concept" onClose={() => setFullOpen(false)} data-id="element-475" data-mp="1nfs2k155zl0l" />}
      </AnimatePresence>

      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-476" data-mp="2b5cv9br4dwn2">
        <InView data-id="element-477" data-mp="2741617c5rdfe">
          <span className="mb-5 inline-flex rounded-full bg-[var(--brand)] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white" data-id="element-478" data-mp="274klmhec5pq9">
            Suggested use case · ESHRE 2027
          </span>
          <SectionHeadline lead="A conversation that starts before ESHRE" follow="— and continues long after." size="medium" data-id="element-479" data-mp="3kd1tm2pji6hn" />
        </InView>
        <InView delay={0.1} data-id="element-480" data-mp="3pezgr8129lfv">
          <p className="text-[15px] leading-[1.7] text-[var(--muted)]" data-id="element-481" data-mp="3ubpxlxkowl2c">
            <span className="font-semibold text-[var(--fg)]" data-id="element-482" data-mp="3u9wqkh65zytt">The IVF Pulse</span> — a connected congress campaign that
            attracts fertility specialists with something valuable, supports meaningful scientific conversations at the
            booth, and turns collective insights into the next reason to engage.
          </p>
        </InView>
      </div>

      <InView delay={0.14} data-id="element-483" data-mp="gw1qzheeqo1a">
        <ol className="mt-10 grid gap-3 sm:grid-cols-3" data-id="element-484" data-mp="gwbjbbmxd7a5">
          {ivfPulseMoments.map(moment => <li key={moment.number} className="rounded-[18px] bg-white p-4 ring-1 ring-[var(--fg)]/[0.08]" data-id="element-485" data-mp="lu38hxw6qfan">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.2em]" style={{
            color: moment.accent
          }} data-id="element-486" data-mp="qswkflvtpy8p">
                {moment.number} · {moment.moment}
              </p>
              <p className="mt-1 text-[18px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-487" data-mp="3sf9bkjr3zz73">{moment.name}</p>
              <p className="text-[13px] text-[var(--muted)]" data-id="element-488" data-mp="6686yn7woad3">{moment.verbs}</p>
            </li>)}
        </ol>
      </InView>

      <InView delay={0.2} y={26} className="mt-6" data-id="element-489" data-mp="jwle4ncz3l1d">
        <div className="overflow-hidden rounded-[22px] bg-white shadow-[0_50px_90px_-60px_rgba(14,27,51,0.6)] ring-1 ring-[var(--fg)]/10" data-id="element-490" data-mp="jzfzqi7q3bwd">
          <div className="flex items-center gap-3 border-b border-[var(--fg)]/[0.08] px-4 py-2.5" data-id="element-491" data-mp="tw2i90cktqv8">
            <div className="flex gap-1.5" aria-hidden="true" data-id="element-492" data-mp="owg4mpsu68lw">
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-493" data-mp="2vn5svpmgv5fs" />
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-494" data-mp="2vnfi3azot8kf" />
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-495" data-mp="2qpdzssa35da0" />
            </div>
            <span className="hidden items-center gap-1.5 truncate rounded-full bg-[var(--surface)] px-3 py-1 text-[10.5px] font-medium text-[var(--muted)] sm:inline-flex" data-id="element-496" data-mp="167d004f4qw51">
              <MousePointerClickIcon className="h-3 w-3 text-[var(--brand)]" aria-hidden="true" data-id="element-497" data-mp="wgqqmry0b1us" />
              Live campaign prototype — click a device to explore each moment
            </span>
            <button type="button" onClick={() => setFullOpen(true)} className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-full bg-[var(--fg)] px-3.5 text-[11px] font-bold text-white transition-colors hover:bg-[var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-498" data-mp="nf0zln81e4xj">
              <Maximize2Icon className="h-3 w-3" aria-hidden="true" data-id="element-499" data-mp="27vnisaphm76d" />
              Full screen
            </button>
          </div>
          <iframe src={ivfPulseUrl} title="The IVF Pulse — ESHRE 2027 connected congress campaign" className="block h-[min(82vh,820px)] min-h-[560px] w-full border-0 bg-white" allow="autoplay; fullscreen" data-id="element-500" data-mp="2ctfgjlctcnih" />
        </div>
      </InView>

      <InView delay={0.3} data-id="element-501" data-mp="3qhuv9nyiwpt">
        <p className="mt-10 border-t border-[var(--fg)]/10 pt-8 text-[24px] font-extrabold leading-[1.15] tracking-[-0.03em] text-[var(--fg)] sm:text-[30px]" data-id="element-502" data-mp="3ppxpuepoqxf">
          Every interaction creates a signal.{' '}
          <span className="text-[var(--brand)]" data-id="element-503" data-mp="3q0u57bk623dk">Every signal makes the next conversation more valuable.</span>
        </p>
        <p className="mt-3 text-[11px] leading-relaxed text-[var(--muted)]" data-id="element-504" data-mp="3g8xw75gjksuk">
          Creative campaign prototype. Peer figures are illustrative, and all medical and product content is subject to
          Ferring medical, legal and regulatory review.
        </p>
      </InView>
    </Section>;
}